import type { GridPaginationModel, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import type {
    GridColDef,
    GridColumnVisibilityModel,
    GridRenderCellParams,
    GridRenderEditCellParams,
    GridRowId
} from '@mui/x-data-grid-pro';
import { isEmpty } from 'lodash';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import MaterialCellRenderer from '../../pipeline/materialCellRenderer';
import { MATERIAL_COLUMN } from '../../production/helpers/productionImportUtils';
import { STATE_TYPE } from '../../production/productionConstants';
import { rowsToContacts } from '../../supply/opportunity/details/ContactListUtils';
import { UserContext } from '../auth/UserContext';
import type { ActionType } from '../components/ActionType';
import { SELECTION_MODEL_TYPES } from '../constants/constants';
import FormModal from '../form/FormModal';
import I18n from '../i18n/I18n';
import { Icons } from '../icons/Icons';
import { V } from '../Layout';
import { MY_COLUMNS } from '../list/columnVisibilitySelectorConstants';
import { emptySelectionModel } from '../list/DataGrid/dataGridUtils';
import FileUploadSection from './SRDataImportComponents/FileUploadSection';
import SelectionOrFinalPreviewSection from './SRDataImportComponents/SelectionAndReviewSection';
import SRDataImportLoader from './SRDataImportLoader';
import type { PredefinedValuesByColumn } from './types';
import { useImportPreviewData } from './useImportPreviewData';
import { filterImportColumnsByPreference } from './utils/SRDataImportUtils';

export type SRDataImportProps = {
    feature: string;
    onSave: (contacts: unknown[]) => void;
    onClose: () => void;
    isUpload?: boolean;
    primaryColDefs: GridColDef[];
    altColDefLabel?: string;
    altColDefs: GridColDef[];
    columnVisibility?: Record<string, boolean>;
    doSave?: (...args: unknown[]) => void;
    locationSearch?: boolean;
    bulkSave?: (...args: unknown[]) => unknown;
    placeholder?: string;
    title?: string;
    saveLabel?: string;
    editLabel?: string;
    setImportDetails?: (details: unknown) => void;
    setShowImportDetailsDialog?: (open: boolean) => void;
    disableTextBasedSelector?: boolean;
    disableBulkSave?: boolean;
    validateImportedData?: (...args: unknown[]) => void;
    existingData?: unknown[];
    apiData?: string;
    hasDataToImport?: boolean;
    hasAIDataToImport?: boolean;
    isAIImportFlow?: boolean;
    dataSource?: string;
    onReset?: () => void;
    isDataSourceOption?: boolean;
    category?: string;
    state?: string;
    isNotReady?: boolean;
    predefinedValuesByColumn?: PredefinedValuesByColumn;
};

export default function SRDataImport({
    feature,
    onSave,
    onClose,
    isUpload,
    primaryColDefs,
    altColDefLabel = undefined,
    altColDefs,
    columnVisibility = {},
    doSave = undefined,
    locationSearch = false,
    bulkSave = undefined,
    placeholder = '',
    title = 'Add',
    saveLabel = 'Update',
    editLabel = 'Edit',
    setImportDetails = undefined,
    setShowImportDetailsDialog = undefined,
    disableTextBasedSelector = false,
    disableBulkSave = false,
    validateImportedData = undefined,
    existingData = [],
    apiData = undefined,
    hasDataToImport = false,
    hasAIDataToImport = false,
    dataSource = undefined,
    onReset = undefined,
    isDataSourceOption = false,
    category = undefined,
    state = '',
    isNotReady = false,
    predefinedValuesByColumn = undefined
}: SRDataImportProps) {
    const [isSaving, setIsSaving] = useState(false);
    const [, setIsPreview] = useState(false);
    const [openUploadDialog, setOpenUploadDialog] = useState(isUpload);
    const [hasHeaders, setHasHeaders] = useState(false);
    const [isAltColDef, setIsAltColDef] = useState(false);
    const [text, setText] = useState(apiData || '');
    const [defaultColumns, setDefaultColumns] = useState<GridColDef[]>(primaryColDefs);
    const [, setSearchLocations] = useState([]);
    const [, setErrors] = useState(undefined);
    const [isDataImported, setIsDataImported] = useState(false);
    const [, setHasHeadersInTable] = useState(false);
    const [invalidData, setInvalidData] = useState(false);
    const [externalColumnVisibilityModel, setExternalColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
        ...columnVisibility
    });
    const [isReviewAndEdit, setIsReviewAndEdit] = useState(false);
    const [pageSize, setPageSize] = useState(24);
    const [page, setPage] = useState(0);
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(undefined);
    const [isSelectScreenActive, setIsSelectScreenActive] = useState(false);
    const [isCsvUploadScreenActive, setIsCsvUploadScreenActive] = useState(!hasDataToImport && !hasAIDataToImport);
    const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(emptySelectionModel);
    const [saveConfirmationOpen, setSaveConfirmationOpen] = useState<boolean>(false);
    const { activeOrganizationAccount, getPreference } = useContext(UserContext);
    const columnPreferences = getPreference('data-import-grid', MY_COLUMNS);
    const {
        attachmentReferenceEditor,
        createPreview,
        fileTobeUploaded,
        isEveryFileUploaded: isEveryFileUploadedForSelectedRow,
        getSelectedAttachments,
        handleDelete,
        handleFileUploadChange,
        handleAttachmentRemoveFromImport,
        materialAttachmentData,
        rows,
        selectedRows,
        setSelectedRows
    } = useImportPreviewData({
        activeOrganizationAccount,
        columnPreferences,
        defaultColumns,
        externalColumnVisibilityModel,
        hasDataToImport,
        hasAIDataToImport,
        hasHeaders,
        state,
        text,
        isNotReady,
        predefinedValuesByColumn
    });

    const updateMaterialColDefination = useCallback(
        (colDefs: GridColDef[]) => {
            return colDefs.map((colDef) => {
                if (colDef.field !== MATERIAL_COLUMN) {
                    return colDef;
                }

                return {
                    ...colDef,
                    renderCell: (params: GridRenderCellParams) => <MaterialCellRenderer value={params.value} />,
                    renderEditCell: (params: GridRenderEditCellParams) => (
                        <MaterialCellRenderer
                            value={params.value}
                            isEditable
                            onAttachmentRemove={handleAttachmentRemoveFromImport}
                        />
                    )
                };
            });
        },
        [handleAttachmentRemoveFromImport]
    );

    useEffect(() => {
        const activeColDefs = isAltColDef ? altColDefs : primaryColDefs;
        setDefaultColumns(updateMaterialColDefination(activeColDefs));
    }, [altColDefs, isAltColDef, primaryColDefs, updateMaterialColDefination]);

    const showColumnsBasedOnPreference = useCallback(
        (defaultColumns: GridColDef[]) => filterImportColumnsByPreference(defaultColumns, columnPreferences),
        [columnPreferences]
    );

    useEffect(() => {
        if (validateImportedData) {
            validateImportedData(rows, existingData, setInvalidData, setErrors);
        }
        if (locationSearch && rows.length) {
            const locationS = rows.filter((row) => {
                return !isEmpty(row.location);
            });
            setSearchLocations(locationS);
        }
        if (isSelectScreenActive) {
            const selectedIds = rows.map((r) => r.id);
            setSelectionModel({ ids: new Set(selectedIds), type: SELECTION_MODEL_TYPES.include });
            setSelectedRows(rows);
        }
    }, [rows, isSelectScreenActive]);

    const handleSelectionChange = ({ ids, type }) => {
        const selectedIDs = new Set<GridRowId>(ids);
        setSelectionModel({ ids: selectedIDs, type });
        const selectedRows = rows.filter((r) =>
            type === SELECTION_MODEL_TYPES.include ? selectedIDs.has(r.id) : !selectedIDs.has(r.id)
        );
        setSelectedRows(selectedRows);
    };

    const uploadAction: ActionType = useMemo(
        () => ({
            label: 'Import',
            Icon: Icons.FileUpload,
            onClick: () => {
                setOpenUploadDialog(true);
                //Import process starts again
                if (isDataImported) {
                    setIsPreview(false);
                    setIsDataImported(false);
                    setHasHeadersInTable(false);
                    setText('');
                }
            }
        }),
        [isDataImported]
    );

    const navigateBackAction: ActionType = useMemo(
        () => ({
            label: <I18n token="import.contacts.uploadfile.modal.back" />,
            color: 'inherit',
            onClick: () => {
                if (isReviewAndEdit) {
                    setIsReviewAndEdit(false);
                    setIsSelectScreenActive(true);
                } else if (isSelectScreenActive) {
                    if (hasDataToImport && !hasAIDataToImport) {
                        setIsSelectScreenActive(false);
                        onReset?.();
                    } else {
                        setInvalidData(false);
                        setSelectionModel(emptySelectionModel);
                        setIsSelectScreenActive(false);
                        setIsCsvUploadScreenActive(true);
                    }
                } else {
                    setSelectionModel(emptySelectionModel);
                    setSelectedRows([]);
                    setIsCsvUploadScreenActive(false);
                    onReset?.();
                }
            }
        }),
        [isReviewAndEdit, isSelectScreenActive, hasDataToImport, hasAIDataToImport, onReset, setSelectedRows]
    );

    useEffect(() => {
        const fetchData = async () => {
            if (isDataImported && !isNotReady) {
                await createPreview();
                setIsPreview(true);
            }
        };
        fetchData();
    }, [createPreview, isDataImported, isNotReady]);

    const EmptyNoRowsOverlay = () => {
        return <span></span>;
    };

    useEffect(() => {
        const fetchData = async () => {
            if ((hasDataToImport || hasAIDataToImport) && !isNotReady) {
                setOpenUploadDialog(false);
                await createPreview();
                setIsSelectScreenActive(true);
            }
        };
        fetchData();
    }, [createPreview, hasDataToImport, hasAIDataToImport, isNotReady]);

    const handleDoSave = useCallback(() => {
        doSave(
            setIsSaving,
            selectedRows,
            isAltColDef,
            altColDefs,
            primaryColDefs,
            defaultColumns,
            setErrors,
            onClose,
            onSave,
            bulkSave,
            activeOrganizationAccount,
            setImportDetails,
            setShowImportDetailsDialog,
            state,
            getSelectedAttachments(),
            attachmentReferenceEditor
        );
    }, [
        doSave,
        selectedRows,
        isAltColDef,
        altColDefs,
        primaryColDefs,
        defaultColumns,
        onClose,
        onSave,
        bulkSave,
        activeOrganizationAccount,
        setImportDetails,
        setShowImportDetailsDialog,
        state,
        getSelectedAttachments,
        attachmentReferenceEditor
    ]);

    const handleSave = useCallback(async () => {
        if (isNotReady) {
            return;
        }
        if (isReviewAndEdit) {
            const useLocalSave = disableBulkSave || !doSave || !bulkSave;
            if (useLocalSave) {
                onClose();
                const contacts = rowsToContacts(selectedRows, isAltColDef ? altColDefs : primaryColDefs);
                onSave(contacts);
            } else {
                if (state === STATE_TYPE.pipeline && !isEveryFileUploadedForSelectedRow()) {
                    setSaveConfirmationOpen(true);
                } else {
                    handleDoSave();
                }
            }
        } else if (isSelectScreenActive) {
            setIsSelectScreenActive(false);
            setIsReviewAndEdit(true);
        } else {
            setOpenUploadDialog(false);
            setIsCsvUploadScreenActive(false);
            await createPreview();
            setIsSelectScreenActive(true);
        }
    }, [
        isNotReady,
        isReviewAndEdit,
        isSelectScreenActive,
        disableBulkSave,
        doSave,
        bulkSave,
        onClose,
        selectedRows,
        isAltColDef,
        altColDefs,
        primaryColDefs,
        onSave,
        state,
        isEveryFileUploadedForSelectedRow,
        handleDoSave,
        createPreview
    ]);

    const handleClose = () => {
        setSelectedRows([]);
        setIsReviewAndEdit(false);
        setIsSelectScreenActive(false);
        setIsCsvUploadScreenActive(false);
        onClose();
    };

    const additionalLeftsideActions = useMemo(
        () =>
            isDataSourceOption ? [navigateBackAction] : isCsvUploadScreenActive || isSaving ? [] : [navigateBackAction],
        [isDataSourceOption, navigateBackAction, isCsvUploadScreenActive, isSaving]
    );

    const additionalActions = useMemo(
        () => (!isReviewAndEdit && !isSelectScreenActive && !isDataImported ? [uploadAction] : []),
        [isReviewAndEdit, isSelectScreenActive, isDataImported, uploadAction]
    );

    return (
        <FormModal
            id={'import-dialog'}
            width={'80%'}
            title={title}
            isOpen={true}
            expanded={true}
            onClose={handleClose}
            isLoading={false}
            saveLabel={isReviewAndEdit ? saveLabel : <I18n token="dialog.next" />}
            loadingButtonVariant={'contained'}
            isSaveDisabled={
                isNotReady ||
                isSaving ||
                invalidData ||
                text?.length === 0 ||
                (isReviewAndEdit && selectedRows.length === 0)
            }
            onSave={handleSave}
            isSaving={false}
            additionalLeftsideActions={additionalLeftsideActions}
            additionalActions={additionalActions}
        >
            <V sx={{ height: '100%', px: 2, gap: 1 }}>
                {isSaving ? (
                    <SRDataImportLoader />
                ) : (
                    <>
                        {isCsvUploadScreenActive && (
                            <FileUploadSection
                                EmptyNoRowsOverlay={EmptyNoRowsOverlay}
                                feature={feature}
                                defaultColumns={defaultColumns}
                                setDefaultColumns={setDefaultColumns}
                                columnVisibility={externalColumnVisibilityModel}
                                text={text}
                                isNotReady={isNotReady}
                                setText={setText}
                                placeholder={placeholder}
                                activeOrganizationAccount={activeOrganizationAccount}
                                openUploadDialog={openUploadDialog}
                                setOpenUploadDialog={setOpenUploadDialog}
                                setIsDataImported={setIsDataImported}
                                disableTextBasedSelector={disableTextBasedSelector}
                                hasDataToImport={hasDataToImport}
                                hasHeaders={hasHeaders}
                                setHasHeaders={setHasHeaders}
                                altColDefLabel={altColDefLabel}
                                isAltColDef={isAltColDef}
                                setIsAltColDef={setIsAltColDef}
                                externalColumnVisibilityModel={externalColumnVisibilityModel}
                                externalOnColumnVisibilityModelChange={setExternalColumnVisibilityModel}
                            />
                        )}
                        {(isSelectScreenActive || isReviewAndEdit) && (
                            <SelectionOrFinalPreviewSection
                                feature={feature}
                                isReviewAndEdit={isReviewAndEdit}
                                category={category}
                                dataSource={dataSource}
                                selectedRows={selectedRows}
                                rows={rows}
                                defaultColumns={defaultColumns}
                                columnPreferences={columnPreferences}
                                showColumnsBasedOnPreference={showColumnsBasedOnPreference}
                                selectionModel={selectionModel}
                                handleSelectionChange={handleSelectionChange}
                                handleDelete={handleDelete}
                                page={page}
                                paginationModel={paginationModel}
                                setPaginationModel={setPaginationModel}
                                setPage={setPage}
                                pageSize={pageSize}
                                setPageSize={setPageSize}
                                setSelectedRows={setSelectedRows}
                                showUploadButton={state === STATE_TYPE.pipeline && fileTobeUploaded.length > 0}
                                handleFileUploadChange={handleFileUploadChange}
                                shouldScrollRight={materialAttachmentData?.size > 0}
                                hasDataToImport={hasDataToImport}
                                externalColumnVisibilityModel={externalColumnVisibilityModel}
                                externalOnColumnVisibilityModelChange={setExternalColumnVisibilityModel}
                                saveConfirmationOpen={saveConfirmationOpen}
                                setSaveConfirmationOpen={setSaveConfirmationOpen}
                                handleDoSave={handleDoSave}
                            />
                        )}
                    </>
                )}
            </V>
        </FormModal>
    );
}
